import { get, post, put, patch, del } from './apiService';
import {
  Organization,
  Location,
  UserActivity,
  AuditLog,
  DashboardStats,
  ApiResponse,
  PaginatedResponse,
  FilterParams,
  PaginationParams,
  User,
  Employee,
} from '../types';

// Dashboard APIs
export const getDashboardStats = async (): Promise<ApiResponse<DashboardStats>> => {
  return get<DashboardStats>('/admin/dashboard/stats');
};

// Organization APIs
export const getOrganizations = async (
  filters?: FilterParams,
  pagination?: PaginationParams
): Promise<PaginatedResponse<Organization>> => {
  const params = new URLSearchParams();

  if (filters?.search) params.append('search', filters.search);
  if (filters?.status) params.append('status', filters.status);
  if (pagination?.page) params.append('page', pagination.page.toString());
  if (pagination?.limit) params.append('limit', pagination.limit.toString());
  if (pagination?.sortBy) params.append('sortBy', pagination.sortBy);
  if (pagination?.sortOrder) params.append('sortOrder', pagination.sortOrder);

  const result = await get<any>(`/admin/organizations?${params.toString()}`);

  // Transform to paginated response
  return {
    success: result.success,
    data: result.data?.data?.organizations || result.data?.organizations || [],
    pagination: result.data?.data?.pagination || result.data?.pagination || {
      page: 1,
      limit: 10,
      total: 0,
      totalPages: 0,
    },
  };
};

export const getOrganizationById = async (orgId: string): Promise<ApiResponse<Organization>> => {
  return get<Organization>(`/admin/organizations/${orgId}`);
};

export const createOrganization = async (
  organization: Partial<Organization>
): Promise<ApiResponse<Organization>> => {
  return post<Organization>('/admin/organizations', organization);
};

export const updateOrganization = async (
  orgId: string,
  updates: Partial<Organization>
): Promise<ApiResponse<Organization>> => {
  return put<Organization>(`/admin/organizations/${orgId}`, updates);
};

export const updateOrganizationStatus = async (
  orgId: string,
  status: Organization['status']
): Promise<ApiResponse<Organization>> => {
  return patch<Organization>(`/admin/organizations/${orgId}/status`, { status });
};

export const deleteOrganization = async (orgId: string): Promise<ApiResponse<void>> => {
  return del<void>(`/admin/organizations/${orgId}`);
};

// Location APIs
export const getAllLocations = async (
  filters?: FilterParams & { orgId?: string },
  pagination?: PaginationParams
): Promise<PaginatedResponse<Location>> => {
  const params = new URLSearchParams();

  if (filters?.search) params.append('search', filters.search);
  if (filters?.status) params.append('status', filters.status);
  if (filters?.orgId) params.append('orgId', filters.orgId);
  if (pagination?.page) params.append('page', pagination.page.toString());
  if (pagination?.limit) params.append('limit', pagination.limit.toString());

  const result = await get<any>(`/admin/locations?${params.toString()}`);

  return {
    success: result.success,
    data: result.data?.data?.locations || result.data?.locations || [],
    pagination: result.data?.data?.pagination || result.data?.pagination || {
      page: 1,
      limit: 10,
      total: 0,
      totalPages: 0,
    },
  };
};

export const getLocationsByOrganization = async (orgId: string): Promise<ApiResponse<Location[]>> => {
  return get<Location[]>(`/admin/organizations/${orgId}/locations`);
};

export const createLocation = async (
  location: Partial<Location>
): Promise<ApiResponse<Location>> => {
  return post<Location>('/admin/locations', location);
};

export const updateLocation = async (
  locationId: number,
  location: Partial<Location>
): Promise<ApiResponse<Location>> => {
  return put<Location>(`/admin/locations/${locationId}`, location);
};

// User Activity APIs
export const getUserActivities = async (
  filters?: FilterParams & { userId?: string; action?: string },
  pagination?: PaginationParams
): Promise<PaginatedResponse<UserActivity>> => {
  const params = new URLSearchParams();

  if (filters?.search) params.append('search', filters.search);
  if (filters?.userId) params.append('userId', filters.userId);
  if (filters?.orgId) params.append('orgId', filters.orgId);
  if (filters?.action) params.append('action', filters.action);
  if (filters?.startDate) params.append('startDate', filters.startDate);
  if (filters?.endDate) params.append('endDate', filters.endDate);
  if (pagination?.page) params.append('page', pagination.page.toString());
  if (pagination?.limit) params.append('limit', pagination.limit.toString());

  const result = await get<any>(`/admin/activities?${params.toString()}`);

  return {
    success: result.success,
    data: result.data?.activities || [],
    pagination: result.data?.pagination || {
      page: 1,
      limit: 10,
      total: 0,
      totalPages: 0,
    },
  };
};

export const getUserLoginHistory = async (
  userId: string,
  startDate?: string,
  endDate?: string
): Promise<ApiResponse<UserActivity[]>> => {
  const params = new URLSearchParams();
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);

  return get<UserActivity[]>(`/admin/users/${userId}/login-history?${params.toString()}`);
};

// Audit Log APIs
export const getAuditLogs = async (
  filters?: FilterParams & { targetType?: string; performedBy?: string },
  pagination?: PaginationParams
): Promise<PaginatedResponse<AuditLog>> => {
  const params = new URLSearchParams();

  if (filters?.search) params.append('search', filters.search);
  if (filters?.targetType) params.append('targetType', filters.targetType);
  if (filters?.performedBy) params.append('performedBy', filters.performedBy);
  if (filters?.startDate) params.append('startDate', filters.startDate);
  if (filters?.endDate) params.append('endDate', filters.endDate);
  if (pagination?.page) params.append('page', pagination.page.toString());
  if (pagination?.limit) params.append('limit', pagination.limit.toString());

  const result = await get<any>(`/admin/audit-logs?${params.toString()}`);

  return {
    success: result.success,
    data: result.data?.logs || [],
    pagination: result.data?.pagination || {
      page: 1,
      limit: 10,
      total: 0,
      totalPages: 0,
    },
  };
};

// User Management APIs
export const getAllUsers = async (
  filters?: FilterParams,
  pagination?: PaginationParams
): Promise<PaginatedResponse<User>> => {
  const params = new URLSearchParams();

  if (filters?.search) params.append('search', filters.search);
  if (filters?.orgId) params.append('orgId', filters.orgId);
  if (pagination?.page) params.append('page', pagination.page.toString());
  if (pagination?.limit) params.append('limit', pagination.limit.toString());

  const result = await get<any>(`/admin/users?${params.toString()}`);

  return {
    success: result.success,
    data: result.data?.users || [],
    pagination: result.data?.pagination || {
      page: 1,
      limit: 10,
      total: 0,
      totalPages: 0,
    },
  };
};

export const getUserById = async (userId: string): Promise<ApiResponse<User>> => {
  return get<User>(`/admin/users/${userId}`);
};

export const searchUsers = async (query: string): Promise<ApiResponse<User[]>> => {
  return get<User[]>(`/admin/users/search?q=${encodeURIComponent(query)}`);
};

export const getUsersByOrganization = async (orgId: string): Promise<ApiResponse<User[]>> => {
  return get<User[]>(`/admin/organizations/${orgId}/users`);
};

// Employee Onboarding APIs
export const getAllEmployees = async (
  filters?: FilterParams,
  pagination?: PaginationParams
): Promise<PaginatedResponse<Employee>> => {
  const params = new URLSearchParams();

  if (filters?.search) params.append('search', filters.search);
  if (filters?.status) params.append('status', filters.status);
  if (filters?.orgId) params.append('orgId', filters.orgId);
  if (pagination?.page) params.append('page', pagination.page.toString());
  if (pagination?.limit) params.append('limit', pagination.limit.toString());
  if (pagination?.sortBy) params.append('sortBy', pagination.sortBy);
  if (pagination?.sortOrder) params.append('sortOrder', pagination.sortOrder);

  const result = await get<any>(`/admin/employees?${params.toString()}`);

  return {
    success: result.success,
    data: result.data?.data?.employees || result.data?.employees || [],
    pagination: result.data?.data?.pagination || result.data?.pagination || {
      page: 1,
      limit: 10,
      total: 0,
      totalPages: 0,
    },
    summary: result.data?.data?.summary || result.data?.summary,
  };
};

export const getEmployeeById = async (employeeId: string): Promise<ApiResponse<Employee>> => {
  return get<Employee>(`/admin/employees/${employeeId}`);
};

export const createEmployee = async (
  employee: Partial<Employee>
): Promise<ApiResponse<Employee>> => {
  return post<Employee>('/admin/employees', employee);
};

export const updateEmployee = async (
  employeeId: string,
  updates: Partial<Employee>
): Promise<ApiResponse<Employee>> => {
  return put<Employee>(`/admin/employees/${employeeId}`, updates);
};

export const updateEmployeeStatus = async (
  employeeId: string,
  status: Employee['status']
): Promise<ApiResponse<Employee>> => {
  return patch<Employee>(`/admin/employees/${employeeId}/status`, { status });
};

export const deleteEmployee = async (employeeId: string): Promise<ApiResponse<void>> => {
  return del<void>(`/admin/employees/${employeeId}`);
};

export const sendOnboardingReminder = async (employeeId: string): Promise<ApiResponse<void>> => {
  return post<void>(`/admin/employees/${employeeId}/reminder`, {});
};

// System Health APIs
export const getSystemHealth = async (): Promise<ApiResponse<any>> => {
  return get<any>('/admin/system/health');
};

// Employee Document Upload APIs
export const getEmployeeDocUploadUrl = async (
  orgId: string,
  employeeId: string,
  documentType: 'profile_photo' | 'aadhar_card' | 'pan_card' | 'address_proof' | 'merged_details',
  fileName: string,
  fileType: string,
  fileSize: number
): Promise<ApiResponse<{
  uploadUrl: string;
  formData: Record<string, string>;
  s3Key: string;
  expiresIn: number;
  bucketName: string;
}>> => {
  return post<any>('/admin/employee-doc-upload-url', {
    orgId,
    employeeId,
    documentType,
    fileName,
    fileType,
    fileSize
  });
};

export const getEmployeeDocDownloadUrl = async (
  orgId: string,
  employeeId: string,
  s3Key: string,
  requestedBy?: string
): Promise<ApiResponse<{
  downloadUrl: string;
  fileName: string;
  expiresIn: number;
  bucketName: string;
}>> => {
  return post<any>('/admin/employee-doc-download-url', {
    orgId,
    employeeId,
    s3Key,
    requestedBy
  });
};

export const uploadFileToS3 = async (
  uploadUrl: string,
  formData: Record<string, string>,
  file: File,
  onProgress?: (percent: number) => void
): Promise<{
  success: boolean;
  error?: string;
}> => {
  return new Promise((resolve) => {
    try {
      const xhr = new XMLHttpRequest();
      const form = new FormData();

      // Add all form fields from presigned POST
      Object.keys(formData).forEach(key => {
        form.append(key, formData[key]);
      });

      // Add the file last
      form.append('file', file);

      // Track upload progress
      if (onProgress) {
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const percent = Math.round((e.loaded / e.total) * 100);
            onProgress(percent);
          }
        });
      }

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve({ success: true });
        } else {
          resolve({
            success: false,
            error: `Upload failed with status ${xhr.status}`
          });
        }
      });

      xhr.addEventListener('error', () => {
        resolve({
          success: false,
          error: 'Network error during upload'
        });
      });

      xhr.addEventListener('abort', () => {
        resolve({
          success: false,
          error: 'Upload was aborted'
        });
      });

      xhr.open('POST', uploadUrl);
      xhr.send(form);
    } catch (error) {
      resolve({
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error during upload'
      });
    }
  });
};

export default {
  getDashboardStats,
  getOrganizations,
  getOrganizationById,
  createOrganization,
  updateOrganization,
  updateOrganizationStatus,
  deleteOrganization,
  getAllLocations,
  getLocationsByOrganization,
  createLocation,
  updateLocation,
  getUserActivities,
  getUserLoginHistory,
  getAuditLogs,
  getAllUsers,
  getUserById,
  searchUsers,
  getUsersByOrganization,
  getAllEmployees,
  getEmployeeById,
  createEmployee,
  updateEmployee,
  updateEmployeeStatus,
  deleteEmployee,
  sendOnboardingReminder,
  getSystemHealth,
  getEmployeeDocUploadUrl,
  getEmployeeDocDownloadUrl,
  uploadFileToS3,
};
